import { Component, Input, ViewChild, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { Subject, takeUntil, catchError, finalize, of } from 'rxjs';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  styleUrl: './invoice-list.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InvoiceListComponent implements OnInit, OnDestroy {
  @Input() organization: MyConfigOrganization;
  @Input() isLoading: boolean = false;
  @Output() loadingChanged = new EventEmitter<boolean>();

  invoices: any[] = [];
  invoiceDataSource: MatTableDataSource<any>;
  invoiceDisplayedColumns: string[] = [
    'created',
    'status',
    'orderNumber',
    'invoiceNumber',
    'customerNumber',
    'amount',
    'ourReference',
    'email'
  ];

  selectedInvoiceStatuses: string[] = [];
  invoiceSearchForm: FormGroup;
  totalInvoices: number = 0;
  searchFieldOptions = [
    { value: 'email', name: 'ORDER_LIST.EMAIL' },
    { value: 'ourReference', name: 'OPERATORS.REFERENCE' }
  ];

  private destroy$ = new Subject<void>();
  private invoiceSort: MatSort;

  @ViewChild(MatPaginator, { static: true }) invoicePaginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    if (sort) {
      this.invoiceSort = sort;
      this.setupInvoiceSorting();
    }
  }

  invoiceStatusOptions: any[] = [
    { value: 'Created', name: 'Created' },
    { value: 'PartiallyPaid', name: 'Partially Paid' },
    { value: 'FullyPaid', name: 'Fully Paid' },
    { value: 'Published', name: 'Published' },
    { value: 'Cancelled', name: 'Cancelled' }
  ];

  constructor(
    private backOfficeService: BackOfficeService,
    private fb: FormBuilder,
    private datepipe: DatePipe
  ) { }

  ngOnInit(): void {
    if (!this.organization) return;

    // Initialize invoice search form
    this.invoiceSearchForm = this.fb.group({
      searchField: ['email'],
      searchValue: ['']
    });

    // Initialize invoice data source
    this.invoiceDataSource = new MatTableDataSource<any>([]);
    if (this.invoicePaginator) {
      this.invoiceDataSource.paginator = this.invoicePaginator;
    }

    // Load invoices on init
    this.loadInvoices();
  }

  loadInvoices(): void {
    this.setLoadingState(true);
    this.loadInvoiceData();
  }

  private loadInvoiceData(): void {
    if (!this.organization) return;

    // Get current sort state
    const sortActive = this.invoiceSort?.active || 'created';
    const sortDirection = this.invoiceSort?.direction || 'desc';

    const selectedField = this.invoiceSearchForm.get('searchField')?.value;
    const selectedValue = this.invoiceSearchForm.get('searchValue')?.value?.trim() || null;

    const requestBody: any = {
      Statuses: this.selectedInvoiceStatuses.length > 0 ? this.selectedInvoiceStatuses : null,
      Paginate: {
        PerPage: this.invoicePaginator?.pageSize || 10,
        Page: this.invoicePaginator ? this.invoicePaginator.pageIndex + 1 : 1
      },
      SortingColumn: sortActive,
      SortingDirection: sortDirection
    };

    // Map selected field to backend properties
    if (selectedField === 'email' && selectedValue) {
      requestBody.Email = selectedValue;
    } else if (selectedField === 'ourReference' && selectedValue) {
      requestBody.OurReference = selectedValue;
    }

    this.backOfficeService.getInvoicesPaginated(requestBody)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading invoices:', error);
          return of({ data: [], pagination: { total: 0 } });
        }),
        finalize(() => this.setLoadingState(false))
      )
      .subscribe((response: any) => {
        if (!response) return;

        this.invoices = response.data || [];
        this.totalInvoices = response.pagination?.total || 0;

        this.setInvoiceTableData();
      });
  }

  private setInvoiceTableData(): void {
    this.invoiceDataSource = new MatTableDataSource<any>(this.invoices);
    this.invoiceDataSource.paginator = this.invoicePaginator;
  }

  private setupInvoiceSorting(): void {
    if (this.invoiceSort) {
      if (!this.invoiceSort.active) {
        this.invoiceSort.active = 'created';
        this.invoiceSort.direction = 'desc';
      }

      this.invoiceSort.sortChange.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.invoicePaginator) {
          this.invoicePaginator.pageIndex = 0;
        }
        this.loadInvoiceData();
      });
    }

    if (this.invoicePaginator) {
      this.invoicePaginator.page.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.loadInvoiceData();
      });
    }
  }

  onInvoiceStatusChange(): void {
    if (this.invoicePaginator) {
      this.invoicePaginator.pageIndex = 0;
    }
    this.loadInvoiceData();
  }

  applyInvoiceFilters(): void {
    if (this.invoicePaginator) {
      this.invoicePaginator.pageIndex = 0;
    }
    this.loadInvoiceData();
  }

  clearInvoiceFilters(): void {
    this.selectedInvoiceStatuses = [];
    this.invoiceSearchForm.patchValue({
      searchField: 'email',
      searchValue: ''
    });
    this.applyInvoiceFilters();
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
    this.loadingChanged.emit(isLoading);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
